import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../theme';

const AdminContainer = styled.div`
  min-height: auto;
  background: transparent;
  padding: 0;
`;

const AdminWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const AdminHeader = styled.div`
  background: ${theme.colors.card};
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 1.5rem;
  border: 1px solid ${theme.colors.border};
`;

const AdminTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const AdminSubtitle = styled.p`
  font-size: 1rem;
  color: ${theme.colors.text.secondary};
`;

const InquiriesGrid = styled.div`
  display: grid;
  gap: 20px;
`;

const InquiryCard = styled.div`
  background: ${theme.colors.card};
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 30px;
  border: 1px solid ${theme.colors.border};
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    background: ${theme.colors.cardHover};
  }
`;

const InquiryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const InquiryInfo = styled.div`
  flex: 1;
`;

const InquiryName = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin-bottom: 8px;
`;

const InquiryEmail = styled.p`
  font-size: 0.95rem;
  color: ${theme.colors.text.secondary};
  margin-bottom: 5px;
`;

const InquiryDate = styled.p`
  font-size: 0.85rem;
  color: ${theme.colors.text.muted};
`;

const StatusBadge = styled.div`
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    switch (props.status) {
      case 'pending':
        return `
          background: linear-gradient(135deg, #f39c12 0%, #e67e22 100%);
          color: white;
        `;
      case 'ongoing':
        return `
          background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
          color: white;
        `;
      case 'require_more_info':
        return `
          background: linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%);
          color: white;
        `;
      case 'resolved':
        return `
          background: linear-gradient(135deg, #27ae60 0%, #229954 100%);
          color: white;
        `;
      default:
        return `
          background: linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%);
          color: white;
        `;
    }
  }}
`;

const InquirySubject = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin-bottom: 15px;
`;

const InquiryMessage = styled.p`
  font-size: 1rem;
  color: ${theme.colors.text.secondary};
  line-height: 1.6;
  margin-bottom: 20px;
  white-space: pre-wrap;
`;

const InquiryActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const StatusSelect = styled.select`
  padding: 10px 15px;
  border: 1px solid ${theme.colors.inputBorder};
  border-radius: 8px;
  font-size: 0.9rem;
  background: ${theme.colors.inputBackground};
  color: ${theme.colors.text.primary};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }

  option {
    background: ${theme.colors.backgroundSolid};
    color: ${theme.colors.text.primary};
  }
`;

const ActionButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  ${props => props.primary ? `
    background: ${theme.colors.primaryGradient};
    color: white;
  ` : `
    background: ${theme.colors.cardHover};
    color: ${theme.colors.text.primary};
    border: 1px solid ${theme.colors.border};
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }
`;

const ResponseModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${theme.colors.overlay};
  display: ${props => props.show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: ${theme.colors.modalBackground};
  backdrop-filter: blur(12px);
  border-radius: 20px;
  padding: 40px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  border: 1px solid ${theme.colors.border};
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin-bottom: 20px;
`;

const ModalLabel = styled.label`
  display: block;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${theme.colors.text.secondary};
  margin-bottom: 8px;
`;

const ModalTextarea = styled.textarea`
  width: 100%;
  padding: 15px;
  border: 1px solid ${theme.colors.inputBorder};
  border-radius: 8px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  min-height: 150px;
  margin-bottom: 20px;
  box-sizing: border-box;
  background: ${theme.colors.inputBackground};
  color: ${theme.colors.text.primary};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  font-size: 1.1rem;
  color: ${theme.colors.text.secondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${theme.colors.text.muted};
`;

const EmptyStateIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 20px;
  opacity: 0.5;
`;

const FilterContainer = styled.div`
  background: ${theme.colors.card};
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
  border: 1px solid ${theme.colors.border};
`;

const FilterLabel = styled.label`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${theme.colors.text.secondary};
`;

function Inquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseText, setResponseText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
      const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/inquiries`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        }
      });

      if (response.ok) {
        const data = await response.json();
        setInquiries(data.data || []);
      } else {
        console.error('Failed to fetch inquiries');
      }
    } catch (error) {
      console.error('Error fetching inquiries:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (inquiryId, newStatus) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
      const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/inquiries/${inquiryId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        await response.json();
        setInquiries(prev => 
          prev.map(inquiry => 
            inquiry.id === inquiryId 
              ? { ...inquiry, status: newStatus, is_resolved: newStatus === 'resolved' }
              : inquiry
          )
        );
      } else {
        console.error('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const sendResponse = async () => {
    if (!selectedInquiry || !responseText.trim()) return;

    setIsSubmitting(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
      const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/inquiries/${selectedInquiry.id}/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ 
          response: responseText,
          inquiry_email: selectedInquiry.email,
          inquiry_name: selectedInquiry.name
        })
      });

      if (response.ok) {
        await response.json();
        setInquiries(prev => 
          prev.map(inquiry => 
            inquiry.id === selectedInquiry.id 
              ? { ...inquiry, admin_response: responseText }
              : inquiry
          )
        );
        setShowResponseModal(false);
        setResponseText('');
        setSelectedInquiry(null);
      } else {
        console.error('Failed to send response');
      }
    } catch (error) {
      console.error('Error sending response:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openResponseModal = (inquiry) => {
    setSelectedInquiry(inquiry);
    setResponseText(inquiry.admin_response || '');
    setShowResponseModal(true);
  };

  const filteredInquiries = filter === 'all' 
    ? inquiries 
    : inquiries.filter(inquiry => inquiry.status === filter);

  if (loading) {
    return (
      <AdminContainer>
        <AdminWrapper>
          <LoadingSpinner>Loading inquiries...</LoadingSpinner>
        </AdminWrapper>
      </AdminContainer>
    );
  }

  return (
    <AdminContainer>
      <AdminWrapper>
        <AdminHeader>
          <AdminTitle>Inquiry Management</AdminTitle>
          <AdminSubtitle>Manage and respond to student inquiries</AdminSubtitle>
        </AdminHeader>

        <FilterContainer>
          <FilterLabel>Filter by status:</FilterLabel>
          <StatusSelect value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All Inquiries</option>
            <option value="pending">Pending</option>
            <option value="ongoing">Ongoing</option>
            <option value="require_more_info">Require More Info</option>
            <option value="resolved">Resolved</option>
          </StatusSelect>
        </FilterContainer>

        <InquiriesGrid>
          {filteredInquiries.length === 0 ? (
            <EmptyState>
              <EmptyStateIcon>📭</EmptyStateIcon>
              <h3>No inquiries found</h3>
              <p>
                {filter === 'all' 
                  ? 'No inquiries have been submitted yet.' 
                  : `No inquiries with status "${filter}" found.`
                }
              </p>
            </EmptyState>
          ) : (
            filteredInquiries.map((inquiry) => (
              <InquiryCard key={inquiry.id}>
                <InquiryHeader>
                  <InquiryInfo>
                    <InquiryName>{inquiry.name}</InquiryName>
                    <InquiryEmail>📧 {inquiry.email}</InquiryEmail>
                    <InquiryDate>📅 {new Date(inquiry.created_at).toLocaleDateString()}</InquiryDate>
                  </InquiryInfo>
                  <StatusBadge status={inquiry.status}>
                    {inquiry.status.replace('_', ' ')}
                  </StatusBadge>
                </InquiryHeader>

                <InquirySubject>📝 {inquiry.subject}</InquirySubject>
                <InquiryMessage>{inquiry.message}</InquiryMessage>

                {inquiry.admin_response && (
                  <div style={{
                    background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                    padding: '20px',
                    borderRadius: '12px',
                    marginBottom: '20px',
                    borderLeft: '4px solid #1976d2',
                    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.15)'
                  }}>
                    <div style={{
                      color: '#1565c0',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>💬</span>
                      <span>Admin Response:</span>
                    </div>
                    <div style={{ 
                      color: '#2c3e50',
                      fontSize: '1rem',
                      lineHeight: '1.6',
                      whiteSpace: 'pre-wrap',
                      background: 'rgba(255, 255, 255, 0.7)',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid rgba(25, 118, 210, 0.2)'
                    }}>
                      {inquiry.admin_response}
                    </div>
                  </div>
                )}

                <InquiryActions>
                  <StatusSelect
                    value={inquiry.status}
                    onChange={(e) => updateStatus(inquiry.id, e.target.value)}
                  >
                    <option value="pending">Pending</option>
                    <option value="ongoing">Ongoing</option>
                    <option value="require_more_info">Require More Info</option>
                    <option value="resolved">Resolved</option>
                  </StatusSelect>

                  <ActionButton
                    primary
                    onClick={() => openResponseModal(inquiry)}
                    disabled={inquiry.is_resolved}
                  >
                    {inquiry.admin_response ? 'Edit Response' : 'Send Response'}
                  </ActionButton>

                  {inquiry.is_resolved && (
                    <ActionButton disabled>
                      ✅ Resolved
                    </ActionButton>
                  )}
                </InquiryActions>
              </InquiryCard>
            ))
          )}
        </InquiriesGrid>

        {showResponseModal && selectedInquiry && (
          <ResponseModal
            show={showResponseModal}
            onClick={() => setShowResponseModal(false)}
          >
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalTitle>Response to {selectedInquiry.name}</ModalTitle>
              
              <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                <strong>Subject:</strong> {selectedInquiry.subject}
                <br />
                <strong>Email:</strong> {selectedInquiry.email}
                <br />
                <strong>Message:</strong>
                <div style={{ marginTop: '8px', whiteSpace: 'pre-wrap' }}>
                  {selectedInquiry.message}
                </div>
              </div>

              <ModalLabel htmlFor="response">Your Response:</ModalLabel>
              <ModalTextarea
                id="response"
                value={responseText}
                onChange={(e) => setResponseText(e.target.value)}
                placeholder="Type your response here..."
              />

              <ModalActions>
                <ActionButton
                  onClick={() => setShowResponseModal(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </ActionButton>
                <ActionButton
                  primary
                  onClick={sendResponse}
                  disabled={isSubmitting || !responseText.trim()}
                >
                  {isSubmitting ? 'Sending...' : 'Send Response'}
                </ActionButton>
              </ModalActions>
            </ModalContent>
          </ResponseModal>
        )}
      </AdminWrapper>
    </AdminContainer>
  );
}

export default Inquiries;
